import { Know } from 'interfaces/Know'
import theme from 'stories/utils/theme'
import HomepageKnowCard from 'stories/V1/components/HomepageKnowCard'
import SectionContainer from 'stories/V1/layouts/SectionContainer'
import styled from 'styled-components'
import { useRef, useState } from 'react'
import { HomepageSubject, getSubjectIcon } from 'lib/constants/homepage'
import _useTranslation from 'lib/hooks/_useTranslation'
import { NewButtonLink } from 'components/elements/NewButton'
import { ButtonText } from 'stories/elements/Typography/Text'
import { seoSubjectsRoute } from 'lib/constants/routes'
import { AnalyticsEventHomepageSubjectsClick } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const SectionWrapper = styled.div`
    width: 100%;
    overflow: hidden;
    padding: 20px;
    @media (max-width: 740px) {
        padding: 0;
    }
`

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    gap: 6px;
    color: ${theme.colors.grayScaleSecondary};

    @media (max-width: 740px) {
        all: unset;
        display: flex;
        flex-wrap: nowrap;
        overflow-x: scroll;
        overflow-y: hidden;
        width: 100%;
        padding: 0 24px;
        gap: 10px;
        -ms-overflow-style: none;
        scrollbar-width: none;
        margin-right: 24px;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-x pan-y;

        &::after {
            content: '';
            flex: 0 0 42px;
            min-width: 42px;
        }

        &::-webkit-scrollbar {
            display: none;
        }
    }
`

const CardWrapper = styled.div`
    transition: opacity 0.3s ease-in-out;
    animation: fadeIn 0.4s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @media (max-width: 740px) {
        flex: 0 0 250px;
        min-width: 250px;
        max-width: 250px;
    }
`

const SubjectsContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 6px;
    flex-wrap: wrap;
    height: 100%;
    svg {
        scale: 0.7;
    }

    @media (max-width: 740px) {
        all: unset;
        display: flex;
        justify-content: flex-start;
        flex-wrap: nowrap;
        overflow-x: scroll;
        overflow-y: hidden;
        width: 100%;
        padding: 0 24px;
        gap: 10px;
        -ms-overflow-style: none;
        scrollbar-width: none;
        margin-left: 42px;
        -webkit-overflow-scrolling: touch;
        touch-action: pan-x pan-y;

        &::after {
            content: '';
            flex: 0 0 42px;
            min-width: 42px;
        }

        &::-webkit-scrollbar {
            display: none;
        }
    }
`

const SubjectButtonContainer = styled.div`
    display: inline-block;

    @media (max-width: 740px) {
        flex: 0 0 auto;
        white-space: nowrap;
    }
`

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    p {
        font-family: var(--font-inter);
        font-variation-settings: 'opsz' 32 !important;
        font-feature-settings: 'calt', 'cv01', 'cv02', 'cv03', 'cv04', 'cv05', 'cv08', 'cv09';
        font-size: 17px;
        line-height: 20px;
        letter-spacing: 0.3px;
        font-weight: 650;
    }
`

const SubjectButton = styled.button<{ isSelected: boolean; color: string }>`
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 0px;
    padding: 4px 14px 4px 6px;
    height: 36px;
    border-radius: ${theme.borderRadius.webappButton};
    border: none;
    background-color: ${(props) => (props.isSelected ? theme.colors.white : theme.colors.backgroundTransparent)};
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        background-color: ${(props) => (props.isSelected ? theme.colors.white : 'rgba(255, 255, 255, 0.3)')};
    }

    p {
        font-feature-settings: 'calt', 'cv01', 'cv02', 'cv03', 'cv04', 'cv05', 'cv08', 'cv09';
        font-variation-settings: 'opsz' 32 !important;
        font-family: var(--font-inter);
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.4px;
        font-weight: ${(props) => (props.isSelected ? 650 : 550)};
        color: ${(props) => (props.isSelected ? theme.colors.black : '#F9F9F9')};
        margin: 0;
    }
`

interface Props {
    subjectKnows: Record<string, Know[]>
    subjects: HomepageSubject[]
}

const HomepageKnows = ({ subjectKnows = {}, subjects = [] }: Props) => {
    const [isAnimating, setIsAnimating] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const { t } = _useTranslation('homepage')

    const translatedSubjects = subjects.map((subject) => ({
        ...subject,
        displayTitle: subject.title || (subject.titleKey ? t(subject.titleKey) : ''),
    }))

    const [selectedSubject, setSelectedSubject] = useState<string | null>(
        translatedSubjects.length > 0 ? translatedSubjects[0].displayTitle : null
    )

    const knowsByDisplayTitle: Record<string, Know[]> = {}
    translatedSubjects.forEach((subject) => {
        const key = subject.titleKey || subject.title || ''
        if (key && subjectKnows[key]) {
            knowsByDisplayTitle[subject.displayTitle] = subjectKnows[key]
        }
    })

    const displayKnows = selectedSubject ? knowsByDisplayTitle[selectedSubject] : []

    const handleSubjectChange = (newSubject: string) => {
        if (selectedSubject !== newSubject) {
            setIsAnimating(true)
            setSelectedSubject(newSubject)
            setTimeout(() => setIsAnimating(false), 400)
        }
    }

    const renderSubjectButton = (subject: HomepageSubject & { displayTitle: string }) => {
        const isSelected = selectedSubject === subject.displayTitle
        const icon = getSubjectIcon(subject.iconType, isSelected)

        return (
            <SubjectButtonContainer key={`subject-${subject.displayTitle}`}>
                <SubjectButton
                    isSelected={isSelected}
                    color={subject.color}
                    onClick={() => handleSubjectChange(subject.displayTitle)}
                >
                    {icon}
                    <p>{subject.displayTitle}</p>
                </SubjectButton>
            </SubjectButtonContainer>
        )
    }

    return (
        <SectionContainer
            backgroundColor={theme.colors.secondarySolidDark}
            title={t('homepage/learningMaterialsHeader')}
            borderRadius="48px"
            fullWidth
            mobileBorderRadius="32px"
            noVerticalPadding
        >
            <>
                {translatedSubjects.length > 0 && (
                    <SubjectsContainer>{translatedSubjects.map(renderSubjectButton)}</SubjectsContainer>
                )}
                <SectionWrapper>
                    <Wrapper ref={wrapperRef}>
                        {displayKnows?.map((know) => (
                            <CardWrapper key={know.slug} style={{ opacity: isAnimating ? 0 : 1 }}>
                                <HomepageKnowCard knowUuid={know.slug} know={know} />
                            </CardWrapper>
                        ))}
                    </Wrapper>
                </SectionWrapper>
                <ButtonWrapper>
                    <span
                        onClick={() => {
                            trackEvent(AnalyticsEventHomepageSubjectsClick, { referrerScreen: 'homepage' })
                        }}
                    >
                        <NewButtonLink
                            color={theme.colors.white}
                            borderRadius={theme.borderRadius.webappButton}
                            width={200}
                            height={44}
                            href={seoSubjectsRoute}
                        >
                            <ButtonText color={theme.colors.headlineDark}>{t('homepage/moreSubjectButtonText')}</ButtonText>
                        </NewButtonLink>
                    </span>
                </ButtonWrapper>
            </>
        </SectionContainer>
    )
}

export default HomepageKnows
